import ChatService from "src/components/pages/home/service/chatService";
import WebSocketManager from "src/services/webSocketManager";
import {
  setErrorMessage,
  setInputText,
  setIsInputDisabled,
  setIsRecentChatsLoaded,
  setRecentChats,
  setRecentInput,
  setShowResult,
  startStreaming,
  addStreamingTextChunk,
  addToolCall,
  updateToolCall,
  updateStreamingMetadata,
  completeStreaming,
  stopStreaming,
} from "src/components/pages/home/store/chatSlice";
import {
  ALIAS,
  CONVERSATION_ID,
  FAILED_QUESTION_COUNT,
  IS_TEXT_ONLY,
  TOOL_NAME,
  TOTAL_SUCCESSFUL_QUESTION_COUNT,
  USER_TEXT,
} from "src/constants/constants";
import { sendCustomRUMEvent } from "src/utils/awsRUM";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { v4 as uuidv4 } from "uuid";
import {
  GENERIC_ERROR_MESSAGE,
  AUTH_ERROR_MESSAGE,
  TIMEOUT_ERROR_MESSAGE,
} from "src/constants/errorMessageConstants";

const NO_MCP_TOOLS = "no_mcp_tools";
const TOOL_NAME_SEPARATOR = ",";

const ChatHandleSubmitStreaming = (prompt: string) => {
  const dispatch = useAppDispatch();
  const { alias } = useAppSelector((state) => state.user);
  const { conversationId } = useAppSelector((state) => state.chatSlice);

  let finalLlmMessage = "";
  let finalSourceAttributions: any[] = [];
  let finalRelatedDocuments: any[] = [];
  let usedToolNames: string[] = [];
  let pendingComplete: any = null;
  let responseReceived = false;
  let timeoutId: NodeJS.Timeout | null = null;

  const handleStreamingEvent = (event: any) => {
    const { type, data } = event;

    switch (type) {
      case "start":
        dispatch(
          startStreaming({
            messageId: data.message_id,
            userText: data.user_text || prompt || "",
          }),
        );
        break;
      case "assistant_message_chunk":
        dispatch(addStreamingTextChunk(data.content || ""));
        break;
      case "tool_use_start":
        if (data.tool_name && !usedToolNames.includes(data.tool_name)) {
          usedToolNames.push(data.tool_name);
        }
        dispatch(
          addToolCall({
            tool_use_id: data.toolUseId || data.tool_id,
            tool_name: data.tool_name,
            tool_input: data.tool_input,
            status: "running",
            timestamp: Date.now(),
          }),
        );
        break;
      case "tool_result":
        dispatch(
          updateToolCall({
            tool_use_id: data.toolUseId || data.tool_id,
            tool_name: data.tool_name || "unknown",
            tool_result: data.content,
            status: data.is_final ? "completed" : "running",
          }),
        );
        break;
      case "response":
        finalLlmMessage = data.llm_message || finalLlmMessage;
        finalSourceAttributions =
          data.source_attributions || finalSourceAttributions;
        finalRelatedDocuments = data.related_documents || finalRelatedDocuments;
        responseReceived = true;

        dispatch(
          updateStreamingMetadata({
            sourceAttributions: finalSourceAttributions,
            relatedDocuments: finalRelatedDocuments,
            csvPreview: data.csv_preview,
            csvData: data.csv_data,
            showCsv: data.show_csv,
          }),
        );

        if (pendingComplete) {
          handleStreamingComplete(pendingComplete);
          pendingComplete = null;
        }
        break;
      case "complete":
        const completeData = {
          message_id: data.message_id,
          successful: data.successful !== false,
          tool_calls_count: data.tool_calls_count || 0,
        };

        if (!responseReceived) {
          pendingComplete = completeData;
        } else {
          handleStreamingComplete(completeData);
        }
        break;
      case "error":
        handleStreamingError(data.message);
        break;
    }
  };

  const handleStreamingComplete = async (data: any) => {
    if (timeoutId) clearTimeout(timeoutId);

    dispatch(
      completeStreaming({
        messageId: data.message_id,
        llmMessage: finalLlmMessage,
      }),
    );

    dispatch(setIsInputDisabled(false));
    dispatch(setInputText(""));

    const recentChats = await ChatService.getRecentChats();
    dispatch(setRecentChats(recentChats.data.recent_messages));
    dispatch(setIsRecentChatsLoaded(true));

    sendCustomRUMEvent(TOTAL_SUCCESSFUL_QUESTION_COUNT, {
      [ALIAS]: alias,
      [USER_TEXT]: prompt,
      [CONVERSATION_ID]: conversationId,
      [IS_TEXT_ONLY]: usedToolNames.length === 0,
      [TOOL_NAME]:
        usedToolNames.length > 0
          ? usedToolNames.join(TOOL_NAME_SEPARATOR)
          : NO_MCP_TOOLS,
    });
  };

  const handleStreamingError = async (error?: any) => {
    if (timeoutId) clearTimeout(timeoutId);

    const isAuthError =
      error?.code === 1008 ||
      error?.code === 1003 ||
      error?.message?.includes("Authorization");

    const errorMessage = isAuthError
      ? AUTH_ERROR_MESSAGE
      : error?.message || GENERIC_ERROR_MESSAGE;

    dispatch(stopStreaming());
    dispatch(setErrorMessage(errorMessage));
    dispatch(setInputText(""));
    dispatch(setIsInputDisabled(false));

    sendCustomRUMEvent(FAILED_QUESTION_COUNT, {
      [ALIAS]: alias,
      [USER_TEXT]: prompt,
      [CONVERSATION_ID]: conversationId,
      [TOOL_NAME]:
        usedToolNames.length > 0
          ? usedToolNames.join(TOOL_NAME_SEPARATOR)
          : NO_MCP_TOOLS,
    });
  };

  return async () => {
    try {
      finalLlmMessage = "";
      finalSourceAttributions = [];
      finalRelatedDocuments = [];
      usedToolNames = [];
      pendingComplete = null;
      responseReceived = false;

      dispatch(stopStreaming());
      dispatch(setShowResult(true));
      dispatch(setIsInputDisabled(true));
      dispatch(setRecentInput(prompt));
      dispatch(setErrorMessage(""));

      // Set timeout for MCP requests (2 minutes)
      timeoutId = setTimeout(() => {
        handleStreamingError({ message: TIMEOUT_ERROR_MESSAGE });
      }, 120000);

      const wsManager = WebSocketManager.getInstance();
      const directWS = await wsManager.getConnection();

      const messageId = `ws-${uuidv4()}`;
      await directWS.sendChatMessage(
        prompt,
        conversationId,
        messageId,
        handleStreamingEvent,
        handleStreamingError,
      );
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      handleStreamingError(error);
    }
  };
};

export default ChatHandleSubmitStreaming;
